import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Form,
    Modal,
    Spinner,
} from 'react-bootstrap';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import EmploymentRequirementsConfirm from '@/components/hiring/EmploymentRequirementsConfirm';
import ReleaseReasonDialog from '@/components/hiring/ReleaseReasonDialog';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import useTermNames from '@/shared/hooks/useTermNames';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import {
    PREVIEW_LOCK_NOTE_ID,
    usePreviewLock,
} from '@/shared/hooks/usePreviewLock';
import { nominationService } from '@/shared/services/nominationService';
import NomineeTermsConfirm, {
    EMPTY_NOMINEE_TERMS,
    nomineeTermsBody,
    openTermsItems,
} from './NomineeTermsConfirm';
import { formatAnswerBy, formatAnswerDay } from '@/shared/utils/deadline';
import {
    EMPTY_EMPLOYMENT_ATTESTATION,
    isEmploymentAttestationComplete,
} from '@/shared/utils/studentEmployment';

const PROGRAM_NAMES = {
    la: 'Learning Assistant',
    si: 'Supplemental Instruction',
    ta: 'Teaching Assistant',
};

const STATUS_BADGES = {
    offered: { bg: 'primary', text: 'Awaiting your answer' },
    accepted: { bg: 'success', text: 'Accepted' },
    declined: { bg: 'secondary', text: 'Declined' },
    released: { bg: 'dark', text: 'Position ended' },
    expired: { bg: 'warning', text: 'Offer expired' },
    taken_back: { bg: 'secondary', text: 'Offer withdrawn' },
};

const GENERIC_ERROR = 'Something went wrong. Try again.';
// Refusals the server names by `code` that say the offer on screen has ended.
const STALE_OFFER_CODES = ['offer_expired', 'offer_taken_back'];

const programName = (nomination) =>
    PROGRAM_NAMES[nomination.programType] || nomination.programType;

// "MTH 1410 section 001 (CRN 12345)", leaving out the parts the position
// does not have.
function labelOf(position) {
    const course = [position?.subject, position?.courseNumber]
        .filter(Boolean)
        .join(' ');
    return [
        course,
        position?.section ? `section ${position.section}` : null,
        position?.crn ? `(CRN ${position.crn})` : null,
    ]
        .filter(Boolean)
        .join(' ');
}

// Whether accepting would pass the weekly hours or the program's positions
// per student, by the commitment the list came with. The server decides; this
// only keeps Accept from offering a click it will refuse.
function overLimit(commitment, programType, hoursPerWeek) {
    const program = commitment?.programs?.[programType];
    if (!commitment || !program) return false;
    const hours = Number.isFinite(hoursPerWeek)
        ? hoursPerWeek
        : (program.hoursPerWeekPerPosition ?? 0);
    if (commitment.heldHours + hours > commitment.limit) return true;
    const cap = program.maxPositionsPerStudent;
    return Number.isFinite(cap) && program.positions >= cap;
}

function commitmentLine(commitment, hoursPerWeek) {
    if (!commitment) return null;
    const adds =
        hoursPerWeek === null || hoursPerWeek === undefined
            ? '.'
            : `; this position adds ${hoursPerWeek}.`;
    return `You hold ${commitment.heldHours} hours a week${adds} Your weekly limit is ${commitment.limit}.`;
}

/**
 * The nominations a faculty member made of the signed-in student, once the
 * program's decisions are released, with the means to accept (confirming the
 * university's student-employment requirements) or decline. Renders nothing
 * while the `hiring` flag is off, and nothing when there is no nomination.
 */
function MyNominationsCard() {
    const enabled = useFeature('hiring');
    const previewLocked = usePreviewLock();
    const [state, setState] = useState({
        status: 'loading',
        nominations: [],
        commitments: {},
    });
    const [active, setActive] = useState(null);
    const [answers, setAnswers] = useState(EMPTY_EMPLOYMENT_ATTESTATION);
    const [terms, setTerms] = useState(EMPTY_NOMINEE_TERMS);
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [leaveError, setLeaveError] = useState('');
    const [modalError, setModalError] = useState('');
    const [stale, setStale] = useState(false);
    const [announcement, setAnnouncement] = useState('');
    const headingRef = useRef(null);
    const focusHeadingAfterClose = useRef(false);
    const modalErrorRef = useRevealAlert(modalError);

    const load = useCallback(async () => {
        try {
            const data = await nominationService.getMine();
            setState({
                status: 'ready',
                nominations: data.nominations || [],
                commitments: data.commitments || {},
            });
        } catch {
            // A reload after an answer keeps the list already on screen.
            setState((previous) =>
                previous.status === 'ready'
                    ? previous
                    : { status: 'error', nominations: [], commitments: {} }
            );
        }
    }, []);

    useEffect(() => {
        if (enabled) load();
    }, [enabled, load]);

    const termName = useTermNames(state.nominations.map((n) => n.term));

    if (!enabled) return null;
    if (state.status === 'loading') {
        return (
            <Spinner animation="border" size="sm" role="status">
                <span className="visually-hidden">
                    Loading your nominations
                </span>
            </Spinner>
        );
    }
    if (state.status === 'error') {
        return (
            <Alert variant="warning">
                We could not load your nominations. Try again later.
            </Alert>
        );
    }
    if (state.nominations.length === 0) return null;

    const open = (type, nomination) => {
        setAnswers(EMPTY_EMPLOYMENT_ATTESTATION);
        setTerms(EMPTY_NOMINEE_TERMS);
        setReason('');
        setModalError('');
        setStale(false);
        setLeaveError('');
        setActive({ type, nomination });
    };

    const close = () => {
        if (!submitting) setActive(null);
    };

    const submit = async (body) => {
        const { nomination, type } = active;
        setSubmitting(true);
        setModalError('');
        try {
            await nominationService.respond(nomination.id, body);
        } catch (error) {
            setModalError(error?.response?.data?.message || GENERIC_ERROR);
            setSubmitting(false);
            // The offer is no longer the one on screen (expired or
            // withdrawn): refresh the card behind the refusal.
            if (STALE_OFFER_CODES.includes(error?.response?.data?.code)) {
                setStale(true);
                load();
            }
            return;
        }
        setSubmitting(false);
        setAnnouncement(
            `You ${type === 'accept' ? 'accepted' : 'declined'} the nomination for ${labelOf(nomination.position)}.`
        );
        focusHeadingAfterClose.current = true;
        setActive(null);
        load();
    };

    const leave = async (reason) => {
        const { nomination } = active;
        setSubmitting(true);
        setLeaveError('');
        try {
            await nominationService.release(nomination.id, reason, {
                status: nomination.status,
            });
        } catch (error) {
            setLeaveError(error?.response?.data?.message || GENERIC_ERROR);
            setSubmitting(false);
            return;
        }
        setSubmitting(false);
        setAnnouncement(
            `You left the nomination for ${labelOf(nomination.position)}.`
        );
        focusHeadingAfterClose.current = true;
        setActive(null);
        load();
    };

    const accept = () =>
        submit({
            action: 'accept',
            employmentAttestation: answers,
            ...nomineeTermsBody(active.nomination, terms),
        });

    const decline = () => {
        const trimmed = reason.trim();
        submit(
            trimmed
                ? { action: 'decline', reason: trimmed }
                : { action: 'decline' }
        );
    };

    const onExited = () => {
        if (!focusHeadingAfterClose.current) return;
        focusHeadingAfterClose.current = false;
        headingRef.current?.focus();
    };

    const selected = active?.nomination;
    const selectedLabel = selected ? labelOf(selected.position) : '';
    const line = selected
        ? commitmentLine(
              state.commitments[selected.term],
              selected.hoursPerWeek
          )
        : null;

    const employmentDone = isEmploymentAttestationComplete(answers);
    const termsOpen =
        active?.type === 'accept' ? openTermsItems(selected, terms) : [];
    const stillToDo = [
        ...(employmentDone ? [] : ['the employment questions']),
        ...termsOpen,
    ];

    const acceptOver = selected
        ? overLimit(
              state.commitments[selected.term],
              selected.programType,
              selected.hoursPerWeek
          )
        : false;

    return (
        <section className="mb-4" aria-labelledby="my-nominations-heading">
            <h2
                id="my-nominations-heading"
                className="h4 mb-3"
                tabIndex={-1}
                ref={headingRef}
            >
                My Nominations
            </h2>
            <div className="visually-hidden" aria-live="polite">
                {announcement}
            </div>

            {state.nominations.map((nomination) => {
                const label = labelOf(nomination.position);
                const name = programName(nomination);
                const badge = STATUS_BADGES[nomination.status];
                return (
                    <Card key={nomination.id} className="mb-3">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <Card.Title as="h3" className="h5 mb-0">
                                    {name}
                                </Card.Title>
                                {badge && (
                                    <Badge bg={badge.bg}>{badge.text}</Badge>
                                )}
                            </div>
                            <p className="mb-1 fw-semibold">{label}</p>
                            {nomination.position?.title && (
                                <p className="mb-1">
                                    {nomination.position.title}
                                </p>
                            )}
                            <p className="mb-1 text-muted">
                                Term {termName(nomination.term)}
                            </p>
                            {nomination.position?.instructorName && (
                                <p className="mb-1">
                                    Instructor:{' '}
                                    {nomination.position.instructorName}
                                </p>
                            )}
                            {nomination.status === 'offered' &&
                                nomination.respondBy && (
                                    <p className="mb-1 fw-semibold">
                                        {`Answer by ${formatAnswerBy(nomination.respondBy)}`}
                                    </p>
                                )}
                            {nomination.status === 'expired' && (
                                <p className="mb-1 fw-semibold">
                                    {`This offer expired${
                                        nomination.respondBy
                                            ? ` on ${formatAnswerDay(nomination.respondBy)}`
                                            : ''
                                    } because it was not answered. The program may offer it again.`}
                                </p>
                            )}
                            {nomination.status === 'taken_back' && (
                                <p className="mb-1 fw-semibold">
                                    The program withdrew this offer.
                                </p>
                            )}
                            {nomination.hoursPerWeek !== null &&
                                nomination.hoursPerWeek !== undefined && (
                                    <p className="mb-1">
                                        {nomination.hoursPerWeek} hours a week
                                    </p>
                                )}
                            {nomination.status === 'accepted' && (
                                <div className="mt-3">
                                    <Button
                                        variant="outline-danger"
                                        disabled={previewLocked}
                                        aria-describedby={
                                            previewLocked
                                                ? PREVIEW_LOCK_NOTE_ID
                                                : undefined
                                        }
                                        onClick={() =>
                                            open('leave', nomination)
                                        }
                                    >
                                        Leave this position
                                        <span className="visually-hidden">
                                            {' '}
                                            {label}
                                        </span>
                                    </Button>
                                    <div className="mt-2">
                                        <PreviewLockNote
                                            locked={previewLocked}
                                        />
                                    </div>
                                </div>
                            )}
                            {nomination.canRespond && (
                                <div className="psh-button-pair mt-3">
                                    <Button
                                        variant="primary"
                                        aria-label={`Accept the ${name} nomination for ${label}`}
                                        onClick={() =>
                                            open('accept', nomination)
                                        }
                                    >
                                        Accept
                                    </Button>
                                    <Button
                                        variant="outline-danger"
                                        aria-label={`Decline the ${name} nomination for ${label}`}
                                        onClick={() =>
                                            open('decline', nomination)
                                        }
                                    >
                                        Decline
                                    </Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                );
            })}

            <ReleaseReasonDialog
                show={active?.type === 'leave'}
                side="student"
                title={`Leave the ${selected ? programName(selected) : ''} position for ${selectedLabel}?`}
                intro="Your assignment ends and your place opens for another student. This can't be undone here."
                confirmLabel="Leave this position"
                busy={submitting}
                refusal={leaveError || null}
                onConfirm={leave}
                onHide={close}
                onExited={onExited}
            />

            <Modal
                show={active?.type === 'accept'}
                onHide={close}
                onExited={onExited}
                animation={false}
                aria-labelledby="accept-nomination-title"
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="accept-nomination-title">
                        Accept this nomination
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="fw-semibold">{selectedLabel}</p>
                    {line && <p>{line}</p>}
                    <EmploymentRequirementsConfirm
                        value={answers}
                        onChange={setAnswers}
                        headingAs="h3"
                        collapsible
                    />
                    <NomineeTermsConfirm
                        nomination={selected}
                        value={terms}
                        onChange={setTerms}
                    />
                </Modal.Body>
                <Modal.Footer className="flex-column align-items-stretch">
                    {acceptOver && (
                        <p
                            id="accept-nomination-over"
                            className="fw-semibold mb-2"
                        >
                            Accepting this would pass the weekly hours or the
                            number of positions allowed for you, so you can not
                            accept it while you hold your other positions.
                            Contact the program if your hours have changed.
                        </p>
                    )}
                    {stillToDo.length > 0 && !acceptOver && (
                        <p
                            id="accept-nomination-todo"
                            className="text-body-secondary small mb-2"
                        >
                            {`To accept, still to answer: ${stillToDo.join(', ')}.`}
                        </p>
                    )}
                    {modalError && (
                        <Alert
                            variant="danger"
                            className="mb-2"
                            ref={modalErrorRef}
                            tabIndex={-1}
                        >
                            {modalError}
                        </Alert>
                    )}
                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="outline-primary" onClick={close}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={accept}
                            aria-describedby={
                                acceptOver
                                    ? 'accept-nomination-over'
                                    : stillToDo.length > 0
                                      ? 'accept-nomination-todo'
                                      : undefined
                            }
                            disabled={
                                submitting ||
                                stale ||
                                acceptOver ||
                                stillToDo.length > 0
                            }
                        >
                            Accept
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            <Modal
                show={active?.type === 'decline'}
                onHide={close}
                onExited={onExited}
                animation={false}
                aria-labelledby="decline-nomination-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="decline-nomination-title">
                        Decline this nomination
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalError && <Alert variant="danger">{modalError}</Alert>}
                    <p className="fw-semibold">{selectedLabel}</p>
                    <Form.Group controlId="nomination-decline-reason">
                        <Form.Label>Reason (optional)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            maxLength={500}
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={close}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={decline}
                        disabled={submitting || stale}
                    >
                        Decline
                    </Button>
                </Modal.Footer>
            </Modal>
        </section>
    );
}

export default MyNominationsCard;
